import {
  Report,
  ReportRequest,
  ReportUpdateRequest,
  ReportStatus,
  Customer,
  CustomerRequest,
  Technician,
  UserResponse,
  ReportMachine,
  ReportMachineRequest,
  ReportTimeLog,
  ReportTimeLogRequest,
  ReportSparePart,
  ReportSparePartRequest,
  CsvImportResult,
  MultiMachineImportResult,
  PantheraImport,
  Attachment,
  AttachmentRequest,
  PageResponse,
  Machine,
  MachineModel,
  SparePart,
  Agent,
  ReportBilling,
  UpdateReportBillingRequest,
  UpdateBillingStatusRequest,
  BillingHistoryEvent,
  ClosureSnapshot
} from '../types';
import { authService } from './authService';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api/v1';

export class ApiClient {
  private static instance: ApiClient;

  private constructor() { }

  static getInstance(): ApiClient {
    if (!ApiClient.instance) {
      ApiClient.instance = new ApiClient();
    }
    return ApiClient.instance;
  }

  private getHeaders(omitContentType = false): HeadersInit {
    const headers: HeadersInit = omitContentType ? {} : {
      'Content-Type': 'application/json',
    };

    const authHeader = authService.getAuthorizationHeader();
    if (authHeader) {
      headers['Authorization'] = authHeader;
    }

    return headers;
  }

  private async fetchWithErrorHandling<T>(url: string, options?: RequestInit): Promise<T> {
    const isMultipart = options?.body instanceof FormData;
    try {
      // Check if token needs refresh before making request
      if (authService.isTokenExpired() && authService.isAuthenticated()) {
        try {
          await authService.refreshToken();
        } catch (refreshError) {
          // If refresh fails, let the request proceed and handle 401
          console.warn('Token refresh failed, proceeding with request');
        }
      }

      const response = await fetch(url, {
        ...options,
        headers: this.getHeaders(isMultipart),
      });

      // Handle 401 Unauthorized - attempt refresh once
      if (response.status === 401 && authService.isAuthenticated()) {
        try {
          await authService.refreshToken();
          // Retry the request with new token
          const retryResponse = await fetch(url, {
            ...options,
            headers: this.getHeaders(isMultipart),
          });

          if (!retryResponse.ok) {
            throw new Error(`HTTP error! status: ${retryResponse.status}`);
          }

          if (retryResponse.status === 204) {
            return undefined as unknown as T;
          }

          return await retryResponse.json();
        } catch (refreshError) {
          authService.logout();
          window.location.href = '/#/login';
          throw new Error('Session expired');
        }
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.message || `HTTP error! status: ${response.status}`);
      }

      if (response.status === 204) {
        return undefined as unknown as T;
      }

      return await response.json();
    } catch (error) {
      console.error('API Error:', error);
      throw error;
    }
  }

  // ============================================
  // Reports
  // ============================================

  async getAllReports(): Promise<Report[]> {
    try {
      const response = await this.fetchWithErrorHandling<PageResponse<Report>>(`${API_BASE_URL}/reports/all`);
      return response.content || [];
    } catch (error) {
      console.error('Error fetching reports:', error);
      throw error;
    }
  }

  async getReportById(id: string): Promise<Report | undefined> {
    try {
      return await this.fetchWithErrorHandling<Report>(`${API_BASE_URL}/reports/${id}`);
    } catch (error) {
      console.error('Error fetching report:', error);
      return undefined;
    }
  }

  async getReportByNumber(number: string): Promise<Report | undefined> {
    try {
      return await this.fetchWithErrorHandling<Report>(`${API_BASE_URL}/reports/number/${encodeURIComponent(number)}`);
    } catch (error) {
      console.error('Error fetching report by number:', error);
      return undefined;
    }
  }

  async getNextReportNumber(agentId: string): Promise<string> {
    try {
      const response = await this.fetchWithErrorHandling<{ nextNumber: string }>(
        `${API_BASE_URL}/reports/next-number?agentId=${encodeURIComponent(agentId)}`
      );
      return response.nextNumber;
    } catch (error) {
      console.error('Error fetching next report number:', error);
      throw error;
    }
  }

  async createReport(report: ReportRequest): Promise<Report> {
    try {
      return await this.fetchWithErrorHandling<Report>(`${API_BASE_URL}/reports`, {
        method: 'POST',
        body: JSON.stringify(report),
      });
    } catch (error) {
      console.error('Error creating report:', error);
      throw error;
    }
  }

  async updateReport(id: string, report: ReportUpdateRequest): Promise<Report> {
    try {
      return await this.fetchWithErrorHandling<Report>(`${API_BASE_URL}/reports/${id}`, {
        method: 'PUT',
        body: JSON.stringify(report),
      });
    } catch (error) {
      console.error('Error updating report:', error);
      throw error;
    }
  }

  async updateReportStatus(id: string, status: ReportStatus, snapshot?: ClosureSnapshot): Promise<Report> {
    try {
      return await this.fetchWithErrorHandling<Report>(
        `${API_BASE_URL}/reports/${id}/status?status=${status}`,
        {
          method: 'PATCH',
          body: snapshot ? JSON.stringify(snapshot) : undefined,
        }
      );
    } catch (error) {
      console.error('Error updating report status:', error);
      throw error;
    }
  }

  // ============================================
  // Billing (T-017)
  // ============================================

  /** null = report non billing-eligible (404: nessun ruolo, nessuna pagina di fatturazione). */
  async getReportBilling(reportId: string): Promise<ReportBilling | null> {
    try {
      return await this.fetchWithErrorHandling<ReportBilling>(`${API_BASE_URL}/reports/${reportId}/billing`);
    } catch (error) {
      console.error('Error fetching report billing:', error);
      return null;
    }
  }

  async updateReportBilling(reportId: string, request: UpdateReportBillingRequest): Promise<ReportBilling> {
    try {
      return await this.fetchWithErrorHandling<ReportBilling>(`${API_BASE_URL}/reports/${reportId}/billing`, {
        method: 'PUT',
        body: JSON.stringify(request),
      });
    } catch (error) {
      console.error('Error updating report billing:', error);
      throw error;
    }
  }

  async updateReportBillingStatus(reportId: string, request: UpdateBillingStatusRequest): Promise<ReportBilling> {
    try {
      return await this.fetchWithErrorHandling<ReportBilling>(`${API_BASE_URL}/reports/${reportId}/billing/status`, {
        method: 'PATCH',
        body: JSON.stringify(request),
      });
    } catch (error) {
      console.error('Error updating report billing status:', error);
      throw error;
    }
  }

  async getReportBillingHistory(reportId: string): Promise<BillingHistoryEvent[]> {
    try {
      return await this.fetchWithErrorHandling<BillingHistoryEvent[]>(`${API_BASE_URL}/reports/${reportId}/billing/history`);
    } catch (error) {
      console.error('Error fetching report billing history:', error);
      throw error;
    }
  }

  async deleteReport(id: string): Promise<void> {
    try {
      await this.fetchWithErrorHandling<void>(`${API_BASE_URL}/reports/${id}`, {
        method: 'DELETE',
      });
    } catch (error) {
      console.error('Error deleting report:', error);
      throw error;
    }
  }

  async searchReports(query: string): Promise<Report[]> {
    try {
      const response = await this.fetchWithErrorHandling<PageResponse<Report>>(
        `${API_BASE_URL}/reports/search?q=${encodeURIComponent(query)}`
      );
      return response.content || [];
    } catch (error) {
      console.error('Error searching reports:', error);
      throw error;
    }
  }

  async filterReports(filter: Record<string, unknown>): Promise<PageResponse<Report>> {
    try {
      return await this.fetchWithErrorHandling<PageResponse<Report>>(
        `${API_BASE_URL}/reports/filter`,
        {
          method: 'POST',
          body: JSON.stringify(filter),
        }
      );
    } catch (error) {
      console.error('Error filtering reports:', error);
      throw error;
    }
  }

  // ============================================
  // Customers
  // ============================================

  async getAllCustomers(): Promise<Customer[]> {
    try {
      const response = await this.fetchWithErrorHandling<PageResponse<Customer>>(`${API_BASE_URL}/customers`);
      return response.content || [];
    } catch (error) {
      console.error('Error fetching customers:', error);
      throw error;
    }
  }

  async getCustomerById(id: string): Promise<Customer | undefined> {
    try {
      return await this.fetchWithErrorHandling<Customer>(`${API_BASE_URL}/customers/${id}`);
    } catch (error) {
      console.error('Error fetching customer:', error);
      return undefined;
    }
  }

  async createCustomer(customer: CustomerRequest): Promise<Customer> {
    try {
      return await this.fetchWithErrorHandling<Customer>(`${API_BASE_URL}/customers`, {
        method: 'POST',
        body: JSON.stringify(customer),
      });
    } catch (error) {
      console.error('Error creating customer:', error);
      throw error;
    }
  }

  async searchCustomers(query: string): Promise<Customer[]> {
    try {
      const response = await this.fetchWithErrorHandling<PageResponse<Customer>>(
        `${API_BASE_URL}/customers/search?q=${encodeURIComponent(query)}`
      );
      return response.content || [];
    } catch (error) {
      console.error('Error searching customers:', error);
      throw error;
    }
  }

  // ============================================
  // Users / Technicians
  // ============================================

  async getAllUsers(): Promise<UserResponse[]> {
    try {
      const response = await this.fetchWithErrorHandling<PageResponse<UserResponse>>(`${API_BASE_URL}/users`);
      return response.content || [];
    } catch (error) {
      console.error('Error fetching users:', error);
      throw error;
    }
  }

  async getAllTechnicians(): Promise<Technician[]> {
    try {
      // Technicians are users - filter by role if needed
      const users = await this.getAllUsers();
      return users.map(user => ({
        id: user.id,
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        name: `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email,
      }));
    } catch (error) {
      console.error('Error fetching technicians:', error);
      throw error;
    }
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await this.fetchWithErrorHandling<void>(`${API_BASE_URL}/users/me/password`, {
      method: 'PUT',
      body: JSON.stringify({ currentPassword, newPassword }),
    });
  }

  async searchUsers(query: string): Promise<UserResponse[]> {
    try {
      const response = await this.fetchWithErrorHandling<PageResponse<UserResponse>>(
        `${API_BASE_URL}/users/search?q=${encodeURIComponent(query)}&activeOnly=true`
      );
      return response.content || [];
    } catch (error) {
      console.error('Error searching users:', error);
      throw error;
    }
  }

  async searchTechnicians(query: string): Promise<Technician[]> {
    try {
      const users = await this.searchUsers(query);
      return users.map(user => ({
        id: user.id,
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        name: `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email,
      }));
    } catch (error) {
      console.error('Error searching technicians:', error);
      throw error;
    }
  }

  // ============================================
  // Report Machines
  // ============================================

  async getReportMachines(reportId: string): Promise<ReportMachine[]> {
    try {
      const response = await this.fetchWithErrorHandling<PageResponse<ReportMachine>>(
        `${API_BASE_URL}/report-machines/report/${reportId}`
      );
      return response.content || [];
    } catch (error) {
      console.error('Error fetching report machines:', error);
      throw error;
    }
  }

  async getMachineById(machineId: string): Promise<ReportMachine | undefined> {
    try {
      return await this.fetchWithErrorHandling<ReportMachine>(`${API_BASE_URL}/report-machines/${machineId}`);
    } catch (error) {
      console.error('Error fetching machine:', error);
      return undefined;
    }
  }

  async addMachineToReport(machine: ReportMachineRequest): Promise<ReportMachine> {
    try {
      return await this.fetchWithErrorHandling<ReportMachine>(`${API_BASE_URL}/report-machines`, {
        method: 'POST',
        body: JSON.stringify(machine),
      });
    } catch (error) {
      console.error('Error adding machine:', error);
      throw error;
    }
  }

  async updateMachine(id: string, machine: Partial<ReportMachineRequest>): Promise<ReportMachine> {
    try {
      return await this.fetchWithErrorHandling<ReportMachine>(`${API_BASE_URL}/report-machines/${id}`, {
        method: 'PUT',
        body: JSON.stringify(machine),
      });
    } catch (error) {
      console.error('Error updating machine:', error);
      throw error;
    }
  }

  async deleteMachine(id: string): Promise<void> {
    try {
      await this.fetchWithErrorHandling<void>(`${API_BASE_URL}/report-machines/${id}`, {
        method: 'DELETE',
      });
    } catch (error) {
      console.error('Error deleting machine:', error);
      throw error;
    }
  }

  // ============================================
  // Time Logs
  // ============================================

  async getTimeLogsByMachine(reportMachineId: string): Promise<ReportTimeLog[]> {
    try {
      const response = await this.fetchWithErrorHandling<PageResponse<ReportTimeLog>>(
        `${API_BASE_URL}/report-time-logs/machine/${reportMachineId}`
      );
      return response.content || [];
    } catch (error) {
      console.error('Error fetching time logs:', error);
      throw error;
    }
  }

  async addTimeLog(timeLog: ReportTimeLogRequest): Promise<ReportTimeLog> {
    try {
      return await this.fetchWithErrorHandling<ReportTimeLog>(`${API_BASE_URL}/report-time-logs`, {
        method: 'POST',
        body: JSON.stringify(timeLog),
      });
    } catch (error) {
      console.error('Error adding time log:', error);
      throw error;
    }
  }

  async updateTimeLog(id: string, timeLog: Partial<ReportTimeLogRequest>): Promise<ReportTimeLog> {
    try {
      return await this.fetchWithErrorHandling<ReportTimeLog>(`${API_BASE_URL}/report-time-logs/${id}`, {
        method: 'PUT',
        body: JSON.stringify(timeLog),
      });
    } catch (error) {
      console.error('Error updating time log:', error);
      throw error;
    }
  }

  async deleteTimeLog(id: string): Promise<void> {
    try {
      await this.fetchWithErrorHandling<void>(`${API_BASE_URL}/report-time-logs/${id}`, {
        method: 'DELETE',
      });
    } catch (error) {
      console.error('Error deleting time log:', error);
      throw error;
    }
  }

  // ============================================
  // Spare Parts
  // ============================================

  async getSparePartsByMachine(reportMachineId: string): Promise<ReportSparePart[]> {
    try {
      const response = await this.fetchWithErrorHandling<ReportSparePart[]>(
        `${API_BASE_URL}/report-spare-parts/machine/${reportMachineId}`
      );
      return response || [];
    } catch (error) {
      console.error('Error fetching spare parts:', error);
      throw error;
    }
  }

  async addSparePart(sparePart: ReportSparePartRequest): Promise<ReportSparePart> {
    try {
      return await this.fetchWithErrorHandling<ReportSparePart>(`${API_BASE_URL}/report-spare-parts`, {
        method: 'POST',
        body: JSON.stringify(sparePart),
      });
    } catch (error) {
      console.error('Error adding spare part:', error);
      throw error;
    }
  }

  async updateSparePart(id: string, sparePart: Partial<ReportSparePartRequest>): Promise<ReportSparePart> {
    try {
      return await this.fetchWithErrorHandling<ReportSparePart>(`${API_BASE_URL}/report-spare-parts/${id}`, {
        method: 'PUT',
        body: JSON.stringify(sparePart),
      });
    } catch (error) {
      console.error('Error updating spare part:', error);
      throw error;
    }
  }

  async deleteSparePart(id: string): Promise<void> {
    try {
      await this.fetchWithErrorHandling<void>(`${API_BASE_URL}/report-spare-parts/${id}`, {
        method: 'DELETE',
      });
    } catch (error) {
      console.error('Error deleting spare part:', error);
      throw error;
    }
  }

  async importSparePartsFromCsv(reportMachineId: string, file: File): Promise<CsvImportResult> {
    const formData = new FormData();
    formData.append('file', file);
    try {
      return await this.fetchWithErrorHandling<CsvImportResult>(
        `${API_BASE_URL}/report-spare-parts/machine/${reportMachineId}/import`,
        { method: 'POST', body: formData }
      );
    } catch (error) {
      console.error('Error importing spare parts from CSV:', error);
      throw error;
    }
  }

  async importSparePartsMultiMachine(reportId: string, file: File): Promise<MultiMachineImportResult> {
    const formData = new FormData();
    formData.append('file', file);
    try {
      return await this.fetchWithErrorHandling<MultiMachineImportResult>(
        `${API_BASE_URL}/reports/${reportId}/machines/import`,
        { method: 'POST', body: formData }
      );
    } catch (error) {
      console.error('Error importing multi-machine spare parts from CSV:', error);
      throw error;
    }
  }

  async requestPantheraImport(reportId: string, documentNumber: string): Promise<PantheraImport> {
    try {
      return await this.fetchWithErrorHandling<PantheraImport>(
        `${API_BASE_URL}/reports/${reportId}/panthera-imports`,
        { method: 'POST', body: JSON.stringify({ documentNumber }) }
      );
    } catch (error) {
      console.error('Error requesting Panthera import:', error);
      throw error;
    }
  }

  async getPantheraImport(reportId: string, importId: string): Promise<PantheraImport> {
    try {
      return await this.fetchWithErrorHandling<PantheraImport>(
        `${API_BASE_URL}/reports/${reportId}/panthera-imports/${importId}`
      );
    } catch (error) {
      console.error('Error fetching Panthera import:', error);
      throw error;
    }
  }

  // ============================================
  // Attachments
  // ============================================

  async getAttachmentsByReport(reportId: string): Promise<Attachment[]> {
    try {
      const response = await this.fetchWithErrorHandling<Attachment[]>(
        `${API_BASE_URL}/attachments/report/${reportId}`
      );
      return response || [];
    } catch (error) {
      console.error('Error fetching attachments:', error);
      throw error;
    }
  }

  async getAttachmentById(id: string): Promise<Attachment | undefined> {
    try {
      return await this.fetchWithErrorHandling<Attachment>(`${API_BASE_URL}/attachments/${id}`);
    } catch (error) {
      console.error('Error fetching attachment:', error);
      return undefined;
    }
  }

  async addAttachment(attachment: AttachmentRequest): Promise<Attachment> {
    try {
      return await this.fetchWithErrorHandling<Attachment>(`${API_BASE_URL}/attachments`, {
        method: 'POST',
        body: JSON.stringify(attachment),
      });
    } catch (error) {
      console.error('Error adding attachment:', error);
      throw error;
    }
  }

  async updateAttachment(id: string, attachment: Partial<AttachmentRequest>): Promise<Attachment> {
    try {
      return await this.fetchWithErrorHandling<Attachment>(`${API_BASE_URL}/attachments/${id}`, {
        method: 'PUT',
        body: JSON.stringify(attachment),
      });
    } catch (error) {
      console.error('Error updating attachment:', error);
      throw error;
    }
  }

  async deleteAttachment(id: string): Promise<void> {
    try {
      await this.fetchWithErrorHandling<void>(`${API_BASE_URL}/attachments/${id}`, {
        method: 'DELETE',
      });
    } catch (error) {
      console.error('Error deleting attachment:', error);
      throw error;
    }
  }

  // ============================================
  // Machines (Installed machines)
  // ============================================

  async getAllMachines(): Promise<Machine[]> {
    try {
      const response = await this.fetchWithErrorHandling<PageResponse<Machine>>(`${API_BASE_URL}/machines`);
      return response.content || [];
    } catch (error) {
      console.error('Error fetching machines:', error);
      throw error;
    }
  }

  async getMachineByIdCatalog(id: string): Promise<Machine | undefined> {
    try {
      return await this.fetchWithErrorHandling<Machine>(`${API_BASE_URL}/machines/${id}`);
    } catch (error) {
      console.error('Error fetching machine:', error);
      return undefined;
    }
  }

  async searchMachines(query: string): Promise<Machine[]> {
    try {
      const response = await this.fetchWithErrorHandling<PageResponse<Machine>>(
        `${API_BASE_URL}/machines/search?q=${encodeURIComponent(query)}`
      );
      return response.content || [];
    } catch (error) {
      console.error('Error searching machines:', error);
      throw error;
    }
  }

  // ============================================
  // Machine Models (Catalog)
  // ============================================

  async getAllMachineModels(): Promise<MachineModel[]> {
    try {
      const response = await this.fetchWithErrorHandling<PageResponse<MachineModel>>(`${API_BASE_URL}/machine-models`);
      return response.content || [];
    } catch (error) {
      console.error('Error fetching machine models:', error);
      throw error;
    }
  }

  async getMachineModelById(id: string): Promise<MachineModel | undefined> {
    try {
      return await this.fetchWithErrorHandling<MachineModel>(`${API_BASE_URL}/machine-models/${id}`);
    } catch (error) {
      console.error('Error fetching machine model:', error);
      return undefined;
    }
  }

  async searchMachineModels(query: string): Promise<MachineModel[]> {
    try {
      const response = await this.fetchWithErrorHandling<PageResponse<MachineModel>>(
        `${API_BASE_URL}/machine-models/search?q=${encodeURIComponent(query)}`
      );
      return response.content || [];
    } catch (error) {
      console.error('Error searching machine models:', error);
      throw error;
    }
  }

  // ============================================
  // Spare Parts (Catalog)
  // ============================================

  async getAllSparePartsCatalog(): Promise<SparePart[]> {
    try {
      const response = await this.fetchWithErrorHandling<PageResponse<SparePart>>(`${API_BASE_URL}/spare-parts`);
      return response.content || [];
    } catch (error) {
      console.error('Error fetching spare parts catalog:', error);
      throw error;
    }
  }

  async getSparePartCatalogById(id: string): Promise<SparePart | undefined> {
    try {
      return await this.fetchWithErrorHandling<SparePart>(`${API_BASE_URL}/spare-parts/${id}`);
    } catch (error) {
      console.error('Error fetching spare part:', error);
      return undefined;
    }
  }

  async searchSparePartsCatalog(query: string): Promise<SparePart[]> {
    try {
      const response = await this.fetchWithErrorHandling<PageResponse<SparePart>>(
        `${API_BASE_URL}/spare-parts/search?q=${encodeURIComponent(query)}`
      );
      return response.content || [];
    } catch (error) {
      console.error('Error searching spare parts:', error);
      throw error;
    }
  }

  // ============================================
  // Agents
  // ============================================

  async getAllAgents(): Promise<Agent[]> {
    try {
      const response = await this.fetchWithErrorHandling<PageResponse<Agent>>(`${API_BASE_URL}/agents`);
      return response.content || [];
    } catch (error) {
      console.error('Error fetching agents:', error);
      throw error;
    }
  }

  async getAgentById(agentId: string): Promise<Agent | null> {
    try {
      const response = await this.fetchWithErrorHandling<Agent>(`${API_BASE_URL}/agents/${agentId}`);
      return response;
    } catch (error) {
      console.error('Error fetching agent:', error);
      throw error;
    }
  }
}

export const apiClient = ApiClient.getInstance();
